import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

// Opens the shared signup / turn-off modals from any program widget and
// switches them to their success state after a successful turbo-stream
// subscribe or unsubscribe.
export default class extends Controller {
  static targets = [
    "signupModal",
    "turnOffModal",
    "signupProgramType",
    "signupCategory",
    "signupNoun",
    "signupSuccessNoun",
    "signupIcon",
    "turnOffCategory",
    "turnOffNoun",
    "turnOffSuccessCategory",
    "turnOffIcon",
    "turnOffForm",
    "nameField",
    "emailField"
  ]

  static values = {
    name: String,
    email: String,
    destroyUrlTemplate: String
  }

  connect() {
    this.signupBootstrapModal = Modal.getOrCreateInstance(this.signupModalTarget)
    this.turnOffBootstrapModal = Modal.getOrCreateInstance(this.turnOffModalTarget)
    this.resetSignup = () => this.#resetSignup()
    this.resetTurnOff = () => this.#resetTurnOff()
    this.signupModalTarget.addEventListener("hidden.bs.modal", this.resetSignup)
    this.turnOffModalTarget.addEventListener("hidden.bs.modal", this.resetTurnOff)
  }

  disconnect() {
    this.signupModalTarget.removeEventListener("hidden.bs.modal", this.resetSignup)
    this.turnOffModalTarget.removeEventListener("hidden.bs.modal", this.resetTurnOff)
    this.signupBootstrapModal?.dispose()
    this.turnOffBootstrapModal?.dispose()
  }

  openSignup(event) {
    event.preventDefault()
    this.#resetSignup()
    this.#applyCategory(event.currentTarget.dataset, {
      category: [this.signupCategoryTarget],
      noun: [this.signupNounTarget, this.signupSuccessNounTarget],
      icon: this.signupIconTarget
    })
    this.signupProgramTypeTarget.value = event.currentTarget.dataset.programType
    this.nameFieldTarget.value = this.nameValue
    this.emailFieldTarget.value = this.emailValue
    this.#clearSignupErrors()
    this.signupModalTarget.addEventListener(
      "shown.bs.modal",
      () => this.nameFieldTarget.focus(),
      { once: true }
    )
    this.signupBootstrapModal.show()
  }

  openTurnOff(event) {
    event.preventDefault()
    this.#resetTurnOff()
    this.#applyCategory(event.currentTarget.dataset, {
      category: [this.turnOffCategoryTarget, this.turnOffSuccessCategoryTarget],
      noun: [this.turnOffNounTarget],
      icon: this.turnOffIconTarget
    })
    this.turnOffFormTarget.action = this.destroyUrlTemplateValue.replace(
      "PLACEHOLDER",
      encodeURIComponent(event.currentTarget.dataset.programType)
    )
    this.turnOffBootstrapModal.show()
  }

  closeSignup(event) {
    event?.preventDefault()
    this.signupBootstrapModal.hide()
  }

  closeTurnOff(event) {
    event?.preventDefault()
    this.turnOffBootstrapModal.hide()
  }

  onSubmitEnd(event) {
    // Any 2xx counts as success, including the page a redirect lands on (e.g.
    // sign-in after the session expired); only our turbo-stream means it saved.
    const { success, fetchResponse } = event.detail
    if (!success || !fetchResponse?.contentType?.includes("turbo-stream")) return

    if (this.signupModalTarget.contains(event.target)) {
      this.#clearSignupErrors()
      this.#setSignupFieldsDisabled(true)
      this.#showSuccess(this.signupModalTarget)
    } else if (this.turnOffModalTarget.contains(event.target)) {
      this.#showSuccess(this.turnOffModalTarget)
    }
  }

  // The focused submit button is hidden in the success state, so move focus to
  // the banner: screen readers announce it and focus stays inside the modal.
  #showSuccess(modal) {
    modal.classList.add("is-success")
    modal.querySelector(".program-alert-modal-success")?.focus()
  }

  #resetSignup() {
    this.signupModalTarget.classList.remove("is-success")
    this.#setSignupFieldsDisabled(false)
  }

  #resetTurnOff() {
    this.turnOffModalTarget.classList.remove("is-success")
  }

  #setSignupFieldsDisabled(disabled) {
    this.nameFieldTarget.disabled = disabled
    this.emailFieldTarget.disabled = disabled
  }

  #applyCategory(dataset, targets) {
    targets.category.forEach((el) => { el.textContent = dataset.categoryLabel })
    targets.noun.forEach((el) => { el.textContent = dataset.alertNoun })
    targets.icon.className = `ph ${dataset.icon}`
  }

  #clearSignupErrors() {
    const errors = this.signupModalTarget.querySelector("#program-alert-signup-errors")
    if (errors) errors.innerHTML = ""
  }
}
